/**
 * Form10_PatientRegistrationForm.jsx
 * Collection: patient_registrations
 * Redirect: /forms/11-patient-responsibility-fees-v1
 */
import React, { useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { FormShell, Field, SigBlock, saveToMongo, downloadPDF, useToast, C } from "../shared/formKit";

const API_URL = "/api/forms/patient-registration";
const NEXT    = "/forms/11-patient-responsibility-fees-v1";

export default function PatientRegistrationForm({ onSubmit }) {
  const history = useHistory();
  const formRef  = useRef(null);
  const sigRef   = useRef(null);
  const [sigEmpty, setSigEmpty]     = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [savedId, setSavedId]       = useState(null);
  const [showToast, Toast]          = useToast();

  const [form, setForm] = useState({
    // Patient info
    lastName:"", firstName:"", middleName:"",
    address:"", city:"", state:"", zip:"",
    homePhone:"", workPhone:"", mobilePhone:"",
    sex:"", dob:"", ssn:"",
    email:"",

    // Guarantor
    guarantorName:"", guarantorAddress:"",
    guarantorRelationship:"", guarantorDob:"",
    guarantorSsn:"", guarantorPhone:"",

    // Emergency contact
    emergencyName:"", emergencyRelationship:"", emergencyPhone:"",

    // Primary insurance
    primaryInsurancePlan:"",
    primaryPHLastName:"", primaryPHFirstName:"", primaryPHMiddle:"",
    primaryPHAddress:"", primaryPHCity:"", primaryPHState:"", primaryPHZip:"",
    primaryPHDob:"", primaryPHSex:"",
    primaryRelationshipToPatient:"",
    primaryIdCertNo:"", primaryPolicyGroupNo:"",
    primaryEmployerName:"", primaryMobilePhone:"",

    // Secondary insurance
    secondaryInsurancePlan:"",
    secondaryPHLastName:"", secondaryPHFirstName:"", secondaryPHMiddle:"",
    secondaryPHAddress:"", secondaryPHCity:"", secondaryPHState:"", secondaryPHZip:"",
    secondaryPHDob:"", secondaryPHSex:"",
    secondaryRelationshipToPatient:"",
    secondaryIdCertNo:"", secondaryPolicyGroupNo:"",
    secondaryEmployerName:"",

    // Sig block
    responsibleParty:"", relationship:"Self",
    sigDate: new Date().toISOString().split("T")[0],
  });

  const set = (k,v)=>setForm(f=>({...f,[k]:v}));
  const getSig = ()=>(!sigRef.current||sigRef.current.isEmpty())?null:sigRef.current.toDataURL("image/png");

  const buildDoc = ()=>({
    formType:"patient_registration",
    patient:{
      lastName:form.lastName, firstName:form.firstName, middleName:form.middleName,
      address:form.address, city:form.city, state:form.state, zip:form.zip,
      homePhone:form.homePhone, workPhone:form.workPhone, mobilePhone:form.mobilePhone,
      sex:form.sex, dob:form.dob||null, ssn:form.ssn, email:form.email,
    },
    guarantor:{
      name:form.guarantorName, address:form.guarantorAddress,
      relationship:form.guarantorRelationship, dob:form.guarantorDob||null,
      ssn:form.guarantorSsn, phone:form.guarantorPhone,
    },
    emergencyContact:{
      name:form.emergencyName, relationship:form.emergencyRelationship, phone:form.emergencyPhone,
    },
    primaryInsurance:{
      planName:form.primaryInsurancePlan,
      policyHolder:{
        lastName:form.primaryPHLastName, firstName:form.primaryPHFirstName, middleName:form.primaryPHMiddle,
        address:form.primaryPHAddress, city:form.primaryPHCity, state:form.primaryPHState, zip:form.primaryPHZip,
        dob:form.primaryPHDob||null, sex:form.primaryPHSex, mobilePhone:form.primaryMobilePhone,
        employerName:form.primaryEmployerName,
      },
      relationshipToPatient:form.primaryRelationshipToPatient,
      idCertNo:form.primaryIdCertNo,
      policyGroupNo:form.primaryPolicyGroupNo,
    },
    secondaryInsurance:{
      planName:form.secondaryInsurancePlan,
      policyHolder:{
        lastName:form.secondaryPHLastName, firstName:form.secondaryPHFirstName, middleName:form.secondaryPHMiddle,
        address:form.secondaryPHAddress, city:form.secondaryPHCity, state:form.secondaryPHState, zip:form.secondaryPHZip,
        dob:form.secondaryPHDob||null, sex:form.secondaryPHSex, employerName:form.secondaryEmployerName,
      },
      relationshipToPatient:form.secondaryRelationshipToPatient,
      idCertNo:form.secondaryIdCertNo,
      policyGroupNo:form.secondaryPolicyGroupNo,
    },
    authorization:{
      responsibleParty:form.responsibleParty, relationship:form.relationship,
      sigDate:form.sigDate||null, signatureDataURL:getSig(),
    },
    meta:{submittedAt:new Date().toISOString(), formVersion:"1.0.0"},
  });

  const handleSubmit = async ()=>{
    if(sigEmpty){showToast("Please sign before submitting.","error");return;}
    setSubmitting(true);
    try{
      const doc=buildDoc();
      const res=typeof onSubmit==="function"?await onSubmit(doc):await saveToMongo(doc,API_URL);
      if(res?._id)setSavedId(res._id);
      showToast("Saved! Redirecting…","success");
      setTimeout(()=>history.push(NEXT),1800);
    }catch(e){console.error(e);showToast("Save failed.","error");}
    finally{setSubmitting(false);}
  };

  const handleDownload = async ()=>{
    try{await downloadPDF(formRef,"patient-registration.pdf");showToast("Downloaded!");}
    catch{showToast("PDF failed.","error");}
  };

  const ST=({num,label})=><div className="wf-section-title"><span className="num">{num}</span>{label}</div>;

  return (
    <FormShell title="Patient Registration" subtitle="Please review and update the information below to the best of your ability."
      dbLabel="MongoDB · patient_registrations"
      savedId={savedId} formRef={formRef}
      onSubmit={handleSubmit} onDownload={handleDownload} submitting={submitting}>

      <div className="wf-section">
        <ST num="1" label="Patient Information" />
        <div className="wf-grid wf-grid-3">
          <Field label="Last Name"><input type="text" value={form.lastName} onChange={e=>set("lastName",e.target.value)} /></Field>
          <Field label="First Name"><input type="text" value={form.firstName} onChange={e=>set("firstName",e.target.value)} /></Field>
          <Field label="Middle Name"><input type="text" value={form.middleName} onChange={e=>set("middleName",e.target.value)} /></Field>
          <Field label="Address"><input type="text" value={form.address} onChange={e=>set("address",e.target.value)} /></Field>
          <Field label="City"><input type="text" value={form.city} onChange={e=>set("city",e.target.value)} /></Field>
          <Field label="State"><input type="text" value={form.state} onChange={e=>set("state",e.target.value)} /></Field>
          <Field label="Zip"><input type="text" value={form.zip} onChange={e=>set("zip",e.target.value)} /></Field>
          <Field label="Home Phone"><input type="tel" value={form.homePhone} onChange={e=>set("homePhone",e.target.value)} /></Field>
          <Field label="Work Phone"><input type="tel" value={form.workPhone} onChange={e=>set("workPhone",e.target.value)} /></Field>
          <Field label="Mobile Phone"><input type="tel" value={form.mobilePhone} onChange={e=>set("mobilePhone",e.target.value)} /></Field>
          <Field label="Sex">
            <div className="wf-radio-group">
              {["M","F","Other"].map(o=><label className="wf-radio-label" key={o}><input type="radio" name="sex" value={o} checked={form.sex===o} onChange={e=>set("sex",e.target.value)} />{o}</label>)}
            </div>
          </Field>
          <Field label="Date of Birth"><input type="date" value={form.dob} onChange={e=>set("dob",e.target.value)} /></Field>
          <Field label="Patient Email"><input type="email" value={form.email} onChange={e=>set("email",e.target.value)} /></Field>
        </div>
      </div>

      <div className="wf-section">
        <ST num="2" label="Guarantor Information" />
        <div className="wf-grid wf-grid-3">
          <Field label="Guarantor Name"><input type="text" value={form.guarantorName} onChange={e=>set("guarantorName",e.target.value)} /></Field>
          <Field label="Address"><input type="text" value={form.guarantorAddress} onChange={e=>set("guarantorAddress",e.target.value)} /></Field>
          <Field label="Relationship to Patient"><input type="text" value={form.guarantorRelationship} onChange={e=>set("guarantorRelationship",e.target.value)} /></Field>
          <Field label="Date of Birth"><input type="date" value={form.guarantorDob} onChange={e=>set("guarantorDob",e.target.value)} /></Field>
          <Field label="Phone"><input type="tel" value={form.guarantorPhone} onChange={e=>set("guarantorPhone",e.target.value)} /></Field>
        </div>
      </div>

      <div className="wf-section">
        <ST num="3" label="Emergency Contact" />
        <div className="wf-grid wf-grid-3">
          <Field label="Name"><input type="text" value={form.emergencyName} onChange={e=>set("emergencyName",e.target.value)} /></Field>
          <Field label="Relationship"><input type="text" value={form.emergencyRelationship} onChange={e=>set("emergencyRelationship",e.target.value)} /></Field>
          <Field label="Phone"><input type="tel" value={form.emergencyPhone} onChange={e=>set("emergencyPhone",e.target.value)} /></Field>
        </div>
      </div>

      <div className="wf-section">
        <ST num="4" label="Primary Insurance" />
        <div className="wf-grid wf-grid-3">
          <Field label="Insurance Plan Name"><input type="text" value={form.primaryInsurancePlan} onChange={e=>set("primaryInsurancePlan",e.target.value)} /></Field>
          <Field label="Relationship to Policy Holder"><input type="text" value={form.primaryRelationshipToPatient} onChange={e=>set("primaryRelationshipToPatient",e.target.value)} /></Field>
          <Field label="ID / Certification No."><input type="text" value={form.primaryIdCertNo} onChange={e=>set("primaryIdCertNo",e.target.value)} /></Field>
          <Field label="Policy / Group No."><input type="text" value={form.primaryPolicyGroupNo} onChange={e=>set("primaryPolicyGroupNo",e.target.value)} /></Field>
          <Field label="Policy Holder Last Name"><input type="text" value={form.primaryPHLastName} onChange={e=>set("primaryPHLastName",e.target.value)} /></Field>
          <Field label="Policy Holder First Name"><input type="text" value={form.primaryPHFirstName} onChange={e=>set("primaryPHFirstName",e.target.value)} /></Field>
          <Field label="Policy Holder DOB"><input type="date" value={form.primaryPHDob} onChange={e=>set("primaryPHDob",e.target.value)} /></Field>
          <Field label="Policy Holder Sex">
            <div className="wf-radio-group">
              {["M","F"].map(o=><label className="wf-radio-label" key={o}><input type="radio" name="phsex" value={o} checked={form.primaryPHSex===o} onChange={e=>set("primaryPHSex",e.target.value)} />{o}</label>)}
            </div>
          </Field>
          <Field label="Employer Name"><input type="text" value={form.primaryEmployerName} onChange={e=>set("primaryEmployerName",e.target.value)} /></Field>
        </div>
      </div>

      <div className="wf-section">
        <ST num="5" label="Secondary Insurance" />
        <div className="wf-grid wf-grid-3">
          <Field label="Insurance Plan Name"><input type="text" value={form.secondaryInsurancePlan} onChange={e=>set("secondaryInsurancePlan",e.target.value)} /></Field>
          <Field label="Relationship to Policy Holder"><input type="text" value={form.secondaryRelationshipToPatient} onChange={e=>set("secondaryRelationshipToPatient",e.target.value)} /></Field>
          <Field label="ID / Certification No."><input type="text" value={form.secondaryIdCertNo} onChange={e=>set("secondaryIdCertNo",e.target.value)} /></Field>
          <Field label="Policy / Group No."><input type="text" value={form.secondaryPolicyGroupNo} onChange={e=>set("secondaryPolicyGroupNo",e.target.value)} /></Field>
          <Field label="Policy Holder Last Name"><input type="text" value={form.secondaryPHLastName} onChange={e=>set("secondaryPHLastName",e.target.value)} /></Field>
          <Field label="Policy Holder First Name"><input type="text" value={form.secondaryPHFirstName} onChange={e=>set("secondaryPHFirstName",e.target.value)} /></Field>
          <Field label="Policy Holder DOB"><input type="date" value={form.secondaryPHDob} onChange={e=>set("secondaryPHDob",e.target.value)} /></Field>
          <Field label="Policy Holder Sex">
            <div className="wf-radio-group">
              {["M","F"].map(o=><label className="wf-radio-label" key={o}><input type="radio" name="s2phsex" value={o} checked={form.secondaryPHSex===o} onChange={e=>set("secondaryPHSex",e.target.value)} />{o}</label>)}
            </div>
          </Field>
          <Field label="Employer Name"><input type="text" value={form.secondaryEmployerName} onChange={e=>set("secondaryEmployerName",e.target.value)} /></Field>
        </div>
      </div>

      <div className="wf-section">
        <ST num="6" label="Assignment &amp; Release" />
        <div className="wf-policy">
          <p>I hereby assign my insurance benefits to be paid directly to the physician.</p>
          <p>I understand that I am financially responsible for all non-covered services, copays, deductibles and/or coinsurance. I authorize and give consent for my provider to bill me directly for recommended services performed that are not covered under the terms of my health plan.</p>
          <p>I authorize the physician to release any medical information required to process this claim.</p>
          <p>I authorize my provider's office to contact me by telephone to remind me of my appointments. A fee for no-shows may apply.</p>
        </div>
      </div>

      <SigBlock sigRef={sigRef} sigEmpty={sigEmpty} setSigEmpty={setSigEmpty}
        responsibleParty={form.responsibleParty} setResponsibleParty={v=>set("responsibleParty",v)}
        relationship={form.relationship} setRelationship={v=>set("relationship",v)}
        sigDate={form.sigDate} setSigDate={v=>set("sigDate",v)} />

      {Toast}
    </FormShell>
  );
}

/*
── Mongoose Schema ────────────────────────────────────────────────────────────
const PatientRegistrationSchema = new Schema({
  formType: { type:String, default:"patient_registration" },
  patient: { lastName:String, firstName:String, middleName:String, address:String, city:String,
    state:String, zip:String, homePhone:String, workPhone:String, mobilePhone:String,
    sex:String, dob:Date, ssn:String, email:String },
  guarantor: { name:String, address:String, relationship:String, dob:Date, ssn:String, phone:String },
  emergencyContact: { name:String, relationship:String, phone:String },
  primaryInsurance: Schema.Types.Mixed,
  secondaryInsurance: Schema.Types.Mixed,
  authorization: { responsibleParty:String, relationship:String, sigDate:Date, signatureDataURL:String },
  meta: { submittedAt:Date, formVersion:String },
}, { timestamps:true, collection:"patient_registrations" });
*/
